import { useState, type ReactNode } from 'react';
import { text } from '../../../app/i18n';
import { Icon } from '../../../design-system/icons';
import type { ScopeVariable } from '../../editor/condition-model';
import { declaredPages, type Template } from '../layout-model';
import { TextComposer, valueSources } from '../inspector-controls';
import { AddButton, SimpleCondition, ValueInput } from './controls';
import type { StudioCatalog } from './library';
import { useLocales } from './locales';

/**
 * Onglet Réactions de l'éditeur v2 (ADR-146 addendum (14) et (16), planche « EditeurV2-Reactions ») : « Quand
 * [élément] [change] → Alors » quatre actions — fixer une valeur, afficher / masquer / obligatoire / lecture seule,
 * recharger une liste, message ou confirmation —, chacune avec sa condition « Si … ». L'éditeur n'écrit que
 * `reactions` du gabarit (contrat 1.10.0) ; il n'exécute rien : l'interprète est celui du rendu et du serveur.
 * Aucune expression brute, aucun identifiant : des listes de libellés.
 */

export type Reaction = NonNullable<Template['reactions']>[number];
export type ReactionAction = Reaction['actions'][number];
type Trigger = Reaction['trigger'];
type FieldRef = { section_id: string; field_id: string };

export interface FieldChoice extends ScopeVariable {
  section_id: string;
  field_id: string;
  fieldType: string;
  reference_type?: string;
}

/** Champs des parties simples, comme variables et comme cibles (réponses de la fiche). */
export function fieldChoices(template: Template): FieldChoice[] {
  return template.sections
    .filter((section) => section.repeat === undefined && section.series === undefined)
    .flatMap((section) =>
      section.fields.map((entry): FieldChoice => {
        const type = entry.field.type;
        const values = entry.field.validation?.enum_values ?? [];
        const labels = (entry.field.validation?.enum_labels ?? {}) as Record<string, Record<string, string>>;
        return {
          ref: `$answers.${entry.field.field_id}`,
          label: text(entry.field.label, 'fr', 'Champ sans nom'),
          type: type === 'enumeration' ? 'enum' : type === 'boolean' ? 'boolean' : ['number', 'decimal', 'quantity', 'money'].includes(type) ? 'number' : type === 'date' ? 'date' : type === 'string' ? 'string' : 'other',
          ...(type === 'enumeration' ? { options: values.map((value) => ({ value, label: text(labels[value], 'fr', value) })) } : {}),
          section_id: section.section_id,
          field_id: entry.field.field_id,
          fieldType: type,
          ...(entry.field.reference_type === undefined ? {} : { reference_type: entry.field.reference_type }),
        };
      }),
    );
}

const sameRef = (a: FieldRef | undefined, b: FieldRef | undefined): boolean => a !== undefined && b !== undefined && a.section_id === b.section_id && a.field_id === b.field_id;
const reactionsOf = (template: Template): Reaction[] => template.reactions ?? [];

function withReactions(template: Template, reactions: Reaction[]): Template {
  const next = { ...template } as Template & Record<string, unknown>;
  if (reactions.length === 0) delete next['reactions'];
  else next.reactions = reactions;
  return next;
}

function freshReactionId(template: Template, base: string): string {
  const used = new Set(reactionsOf(template).map((reaction) => reaction.reaction_id));
  const stem = base.replace(/[^a-z0-9_]/g, '_').replace(/^[^a-z]+/, '') || 'reaction';
  if (!used.has(stem)) return stem;
  let index = 2;
  while (used.has(`${stem}_${index}`)) index += 1;
  return `${stem}_${index}`;
}

/** Réaction d'un déclencheur (champ qui change, ouverture, validation, ajout d'une ligne), si elle existe. */
export function reactionFor(template: Template, trigger: Trigger): Reaction | undefined {
  return reactionsOf(template).find((reaction) => reaction.trigger.on === trigger.on && (trigger.on !== 'change' || sameRef(reaction.trigger.field, trigger.field)) && (reaction.trigger.page_id ?? '') === (trigger.page_id ?? '') && (reaction.trigger.section_id ?? '') === (trigger.section_id ?? ''));
}

/** Remplace les actions d'une réaction (créée au besoin ; supprimée sans action). */
export function setActions(template: Template, trigger: Trigger, actions: ReactionAction[]): Template {
  const current = reactionFor(template, trigger);
  const others = reactionsOf(template).filter((reaction) => reaction !== current);
  if (actions.length === 0) return withReactions(template, others);
  const base = trigger.on === 'change' ? `quand_${trigger.field?.field_id ?? 'champ'}` : trigger.on === 'row_add' ? `ajout_${trigger.section_id ?? 'ligne'}` : trigger.on === 'open' ? 'ouverture' : 'validation';
  const list0 = actions as Reaction['actions'];
  const reaction: Reaction = current === undefined ? { reaction_id: freshReactionId(template, base), trigger, actions: list0 } : { ...current, actions: list0 };
  const list = reactionsOf(template).map((candidate) => (candidate === current ? reaction : candidate));
  return withReactions(template, current === undefined ? [...others, reaction] : list);
}

export const ACTION_LABELS: Readonly<Record<ReactionAction['action'], string>> = { set: 'Fixer une valeur', state: 'Changer l’état', reload: 'Recharger une liste', message: 'Message ou confirmation' };
const EFFECTS: ReadonlyArray<[NonNullable<ReactionAction['effect']>, string]> = [['show', 'Afficher'], ['hide', 'Masquer'], ['require', 'Rendre obligatoire'], ['readonly', 'Mettre en lecture seule']];

/** Actions admises par moment : état et rechargement sont dérivés (changement, ouverture) ; pas de confirmation à l'ouverture. */
export function allowedActions(on: Trigger['on']): Array<ReactionAction['action']> {
  return on === 'change' || on === 'open' ? ['set', 'state', 'reload', 'message'] : ['set', 'message'];
}

function starter(kind: ReactionAction['action'], fields: readonly FieldChoice[], self: FieldRef | undefined, on: Trigger['on']): ReactionAction {
  const other = fields.find((field) => !sameRef(field, self)) ?? fields[0];
  const target = other === undefined ? undefined : { section_id: other.section_id, field_id: other.field_id };
  switch (kind) {
    case 'set':
      return { action: 'set', ...(target === undefined ? {} : { target }), value: '' } as ReactionAction;
    case 'state':
      return { action: 'state', effect: 'show', ...(target === undefined ? {} : { target }) } as ReactionAction;
    case 'reload': {
      const choice = fields.find((field) => field.fieldType === 'enumeration' && !sameRef(field, self));
      return { action: 'reload', ...(choice === undefined ? {} : { target: { section_id: choice.section_id, field_id: choice.field_id } }), options: { $literal: choice?.options?.map((option) => option.value) ?? [] } } as ReactionAction;
    }
    case 'message':
      return { action: 'message', kind: on === 'open' ? 'info' : 'warning', text: { fr: '' } } as ReactionAction;
  }
}

function TargetPick({ label, value, fields, pages, onChange, filter }: { label: string; value: FieldRef | undefined; fields: readonly FieldChoice[]; pages?: ReadonlyArray<{ id: string; label: string }>; onChange: (target: { field?: FieldRef; page?: string }) => void; filter?: (field: FieldChoice) => boolean }) {
  const shown = filter === undefined ? fields : fields.filter(filter);
  const key = value === undefined ? '' : `${value.section_id}/${value.field_id}`;
  return (
    <select aria-label={label} value={key} onChange={(event) => {
      const raw = event.target.value;
      if (raw.startsWith('page:')) onChange({ page: raw.slice(5) });
      else {
        const [section_id, field_id] = raw.split('/');
        if (section_id !== undefined && field_id !== undefined) onChange({ field: { section_id, field_id } });
      }
    }}>
      {key === '' && <option value="">Choisir…</option>}
      {key !== '' && !shown.some((field) => `${field.section_id}/${field.field_id}` === key) && <option value={key}>Champ qui n’existe plus</option>}
      {shown.map((field) => <option key={`${field.section_id}/${field.field_id}`} value={`${field.section_id}/${field.field_id}`}>{field.label}</option>)}
      {(pages ?? []).map((page) => <option key={page.id} value={`page:${page.id}`}>Page « {page.label} »</option>)}
    </select>
  );
}

function ActionCard({ index, action, fields, variables, pages, template, catalog, self, on, onChange, onRemove }: {
  index: number;
  action: ReactionAction;
  fields: readonly FieldChoice[];
  variables: readonly ScopeVariable[];
  pages: ReadonlyArray<{ id: string; label: string }>;
  template: Template;
  catalog: StudioCatalog;
  self: FieldRef | undefined;
  on: Trigger['on'];
  onChange: (action: ReactionAction) => void;
  onRemove: () => void;
}) {
  const env = useLocales();
  const patch = (change: Record<string, unknown>): void => {
    const next = { ...action, ...change } as Record<string, unknown>;
    for (const [key, value] of Object.entries(change)) if (value === undefined) delete next[key];
    onChange(next as ReactionAction);
  };
  const target = fields.find((field) => sameRef(field, action.target));
  const when = (
    <span className="ay-v2-sentence__when">
      {action.when !== undefined && <SimpleCondition value={action.when} variables={variables} label={`Condition de l’action ${index + 1}`} onChange={(value) => patch({ when: value })} />}
      {action.when !== undefined && <button type="button" className="ay-v2-link" onClick={() => patch({ when: undefined })}>Toujours</button>}
      {action.when === undefined && <button type="button" className="ay-v2-link" onClick={() => patch({ when: variables[0] === undefined ? undefined : { $not: [{ $isEmpty: [variables[0].ref] }] } })}>+ Si…</button>}
    </span>
  );
  let body: ReactNode;
  if (action.action === 'set') {
    const fromField = typeof action.value === 'string' && action.value.startsWith('$answers.');
    body = (
      <>
        {action.when !== undefined && when}
        <span className="ay-v2-sentence">
          <span className="ay-v2-word">mettre</span>
          <TargetPick label="Champ à remplir" value={action.target} fields={fields} filter={(field) => !sameRef(field, self)} onChange={({ field }) => field !== undefined && patch({ target: field })} />
          <span className="ay-v2-word">à</span>
          <select aria-label="Sorte de valeur" value={fromField ? 'field' : 'value'} onChange={(event) => patch({ value: event.target.value === 'field' ? (variables[0]?.ref ?? '') : '' })}>
            <option value="value">une valeur</option>
            <option value="field">la valeur de</option>
          </select>
          {fromField ? (
            <select aria-label="Champ source" value={action.value as string} onChange={(event) => patch({ value: event.target.value })}>
              {variables.map((variable) => <option key={variable.ref} value={variable.ref}>{variable.label}</option>)}
            </select>
          ) : (
            <ValueInput variable={target} value={typeof action.value === 'object' && action.value !== null && '$literal' in (action.value as object) ? (action.value as { $literal: unknown }).$literal : action.value} label="Valeur" onChange={(value) => patch({ value: typeof value === 'string' && value.startsWith('$') ? { $literal: value } : (value as never) })} />
          )}
        </span>
        {action.when === undefined && when}
      </>
    );
  } else if (action.action === 'state') {
    body = (
      <>
        <span className="ay-v2-sentence">
          <select aria-label="Effet" value={action.effect ?? 'show'} onChange={(event) => patch({ effect: event.target.value as never })}>
            {EFFECTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <TargetPick
            label="Élément visé"
            value={action.target}
            fields={fields}
            pages={action.effect === 'show' || action.effect === 'hide' ? pages : []}
            filter={(field) => !sameRef(field, self)}
            onChange={({ field, page }) => (page !== undefined ? patch({ target_page: page, target: undefined }) : field !== undefined && patch({ target: field, target_page: undefined }))}
          />
        </span>
        {when}
        <span className="ay-v2-sentence">
          <span className="ay-v2-word">sinon</span>
          <select aria-label="Sinon" value={action.otherwise ?? 'reverse'} onChange={(event) => patch({ otherwise: event.target.value === 'reverse' ? undefined : (event.target.value as never) })}>
            <option value="reverse">l’inverse</option>
            {(action.effect ?? 'show') === 'show' && <option value="reverse_and_clear">masquer et vider</option>}
          </select>
        </span>
      </>
    );
  } else if (action.action === 'reload') {
    const values = Array.isArray((action.options as { $literal?: unknown } | undefined)?.$literal) ? ((action.options as { $literal: string[] }).$literal) : [];
    const isSelector = target?.fieldType === 'reference';
    const object = catalog.model.find((candidate) => candidate.object_type === target?.reference_type);
    const filterEntry = Object.entries((action.filter ?? {}) as Record<string, { eq?: unknown }>)[0];
    body = (
      <>
        <span className="ay-v2-sentence">
          <span className="ay-v2-word">Recharger les choix de</span>
          <TargetPick label="Liste à recharger" value={action.target} fields={fields} filter={(field) => (field.fieldType === 'enumeration' || field.fieldType === 'reference') && !sameRef(field, self)} onChange={({ field }) => {
            const chosen = fields.find((candidate) => sameRef(candidate, field));
            if (field === undefined) return;
            patch(chosen?.fieldType === 'reference' ? { target: field, options: undefined, filter: {} } : { target: field, filter: undefined, options: { $literal: chosen?.options?.map((option) => option.value) ?? [] } });
          }} />
        </span>
        {target !== undefined && !isSelector && (
          <span className="ay-v2-chips" role="group" aria-label="Choix gardés">
            <span className="ay-v2-word">seulement</span>
            {(target.options ?? []).map((option) => {
              const on = values.includes(option.value);
              return (
                <button key={option.value} type="button" className="ay-studio-chip-button" aria-pressed={on} onClick={() => patch({ options: { $literal: on ? values.filter((value) => value !== option.value) : [...values, option.value] } })}>{option.label}</button>
              );
            })}
          </span>
        )}
        {isSelector && (
          <span className="ay-v2-sentence">
            <span className="ay-v2-word">seulement ceux dont</span>
            <select aria-label="Champ de l’objet" value={filterEntry?.[0] ?? ''} onChange={(event) => patch({ filter: event.target.value === '' ? {} : { [event.target.value]: { eq: filterEntry?.[1]?.eq ?? '' } } })}>
              <option value="">Choisir…</option>
              {(object?.fields ?? []).filter((field) => ['string', 'enumeration', 'boolean', 'reference'].includes(field.type)).map((field) => <option key={field.field_id} value={field.field_id}>{text(field.label, 'fr', 'champ')}</option>)}
            </select>
            <span className="ay-v2-word">est</span>
            <select aria-label="Valeur du filtre" value={typeof filterEntry?.[1]?.eq === 'string' && (filterEntry[1].eq as string).startsWith('$answers.') ? (filterEntry[1].eq as string) : '__value'} onChange={(event) => filterEntry !== undefined && patch({ filter: { [filterEntry[0]]: { eq: event.target.value === '__value' ? '' : event.target.value } } })}>
              <option value="__value">une valeur</option>
              {variables.map((variable) => <option key={variable.ref} value={variable.ref}>la valeur de {variable.label}</option>)}
            </select>
            {filterEntry !== undefined && !(typeof filterEntry[1]?.eq === 'string' && (filterEntry[1].eq as string).startsWith('$answers.')) && (
              <input type="text" aria-label="Valeur" value={String(filterEntry[1]?.eq ?? '')} onChange={(event) => patch({ filter: { [filterEntry[0]]: { eq: event.target.value } } })} />
            )}
          </span>
        )}
        {when}
      </>
    );
  } else {
    const kinds: Array<[NonNullable<ReactionAction['kind']>, string]> = [['info', 'Informer'], ['warning', 'Avertir'], ...(on === 'open' ? [] : [['confirm', 'Demander confirmation'] as [NonNullable<ReactionAction['kind']>, string]])];
    const localized = (action.text ?? {}) as Record<string, unknown>;
    body = (
      <>
        <span className="ay-v2-sentence">
          <select aria-label="Sorte de message" value={action.kind ?? 'info'} onChange={(event) => patch({ kind: event.target.value as never, ...(event.target.value === 'confirm' ? {} : { confirm_label: undefined, cancel_label: undefined }) })}>
            {kinds.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </span>
        {when}
        <TextComposer label="Message" value={localized[env.defaultLocale]} sources={valueSources(template, { aggregates: true, answers: true })} onChange={(value) => patch({ text: { ...localized, [env.defaultLocale]: value ?? '' } as never })} />
        {action.kind === 'confirm' && (
          <span className="ay-v2-sentence">
            <span className="ay-v2-word">Boutons :</span>
            <input type="text" aria-label="Bouton de confirmation" placeholder="Continuer" value={text(action.confirm_label, env.defaultLocale, '')} onChange={(event) => patch({ confirm_label: event.target.value === '' ? undefined : { ...(action.confirm_label ?? {}), [env.defaultLocale]: event.target.value } })} />
            <input type="text" aria-label="Bouton d’annulation" placeholder="Annuler" value={text(action.cancel_label, env.defaultLocale, '')} onChange={(event) => patch({ cancel_label: event.target.value === '' ? undefined : { ...(action.cancel_label ?? {}), [env.defaultLocale]: event.target.value } })} />
          </span>
        )}
      </>
    );
  }
  return (
    <li className="ay-v2-action">
      <header className="ay-v2-action__head">
        <span className="ay-v2-action__index">{index + 1}</span>
        <strong>{ACTION_LABELS[action.action]}</strong>
        <span className="ay-studio__spacer" />
        <button type="button" className="ay-studio__icon ay-studio__icon--sm is-danger" aria-label={`Retirer l’action ${index + 1}`} title="Retirer" onClick={onRemove}>
          <Icon name="trash" size={14} />
        </button>
      </header>
      <div className="ay-v2-action__body">{body}</div>
    </li>
  );
}

/** Éditeur des actions d'un déclencheur (« Quand … → Alors »). */
export function ReactionEditor({ template, apply, trigger, heading, catalog, self }: { template: Template; apply: (next: Template, coalesce?: string) => void; trigger: Trigger; heading: ReactNode; catalog: StudioCatalog; self?: FieldRef }) {
  const [adding, setAdding] = useState(false);
  const reaction = reactionFor(template, trigger);
  const actions = reaction?.actions ?? [];
  const fields = fieldChoices(template);
  const pages = declaredPages(template).map((page) => ({ id: page.page_id, label: text(page.label as Record<string, string>, 'fr', 'Page') }));
  const write = (next: ReactionAction[], coalesce?: string): void => apply(setActions(template, trigger, next), coalesce);
  const allowed = allowedActions(trigger.on);
  return (
    <section className="ay-v2-reaction" aria-label="Réaction">
      <div className="ay-v2-reaction__when">{heading}</div>
      <div className="ay-v2-reaction__then">
        <span className="ay-v2-word">Alors</span>
      </div>
      {actions.length === 0 && <p className="ay-studio-empty">Aucune action.</p>}
      <ol className="ay-v2-actions">
        {actions.map((action, index) => (
          <ActionCard
            key={index}
            index={index}
            action={action}
            fields={fields}
            variables={fields}
            pages={pages}
            template={template}
            catalog={catalog}
            self={self}
            on={trigger.on}
            onChange={(next) => write(actions.map((candidate, position) => (position === index ? next : candidate)), `reaction:${reaction?.reaction_id ?? 'new'}:${index}`)}
            onRemove={() => write(actions.filter((_, position) => position !== index))}
          />
        ))}
      </ol>
      {adding ? (
        <div className="ay-v2-menu" role="menu" aria-label="Ajouter une action">
          {allowed.map((kind) => (
            <button key={kind} type="button" role="menuitem" onClick={() => { setAdding(false); write([...actions, starter(kind, fields, self, trigger.on)]); }}>{ACTION_LABELS[kind]}</button>
          ))}
          <button type="button" role="menuitem" className="ay-v2-link" onClick={() => setAdding(false)}>Annuler</button>
        </div>
      ) : (
        <AddButton label="Ajouter une action" onClick={() => setAdding(true)} disabled={actions.length >= 10} {...(actions.length >= 10 ? { title: 'Dix actions au plus par réaction.' } : {})} />
      )}
    </section>
  );
}

/** Réactions d'un champ : « Quand [champ] change ». */
export function FieldReactions({ template, apply, catalog, sectionId, fieldId, label }: { template: Template; apply: (next: Template, coalesce?: string) => void; catalog: StudioCatalog; sectionId: string; fieldId: string; label: string }) {
  const self = { section_id: sectionId, field_id: fieldId };
  return (
    <ReactionEditor
      template={template}
      apply={apply}
      catalog={catalog}
      self={self}
      trigger={{ on: 'change', field: self }}
      heading={
        <span className="ay-v2-sentence">
          <span className="ay-v2-word">Quand</span>
          <strong className="ay-v2-token">{label}</strong>
          <span className="ay-v2-token is-muted">change</span>
        </span>
      }
    />
  );
}

/** Autres moments du formulaire : ouverture, validation, ajout d'une ligne à chaque liste. */
export function FormMoments({ template, apply, catalog }: { template: Template; apply: (next: Template, coalesce?: string) => void; catalog: StudioCatalog }) {
  const [open, setOpen] = useState<string | null>(null);
  const moments: Array<{ key: string; label: string; trigger: Trigger }> = [
    { key: 'open', label: 'À l’ouverture du formulaire', trigger: { on: 'open' } },
    { key: 'validate', label: 'À la validation du formulaire', trigger: { on: 'page_validate' } },
    ...template.sections.filter((section) => section.series !== undefined).map((section) => ({ key: `row:${section.section_id}`, label: `À l’ajout d’une ligne · ${text(section.label, 'fr', 'liste')}`, trigger: { on: 'row_add' as const, section_id: section.section_id } })),
  ];
  return (
    <ul className="ay-v2-moments">
      {moments.map((moment) => {
        const count = reactionFor(template, moment.trigger)?.actions.length ?? 0;
        const expanded = open === moment.key;
        return (
          <li key={moment.key}>
            <button type="button" className="ay-v2-moment" aria-expanded={expanded} onClick={() => setOpen(expanded ? null : moment.key)}>
              <span>{moment.label}</span>
              <span className="ay-studio__muted">{count === 0 ? 'aucune' : `${count} action${count > 1 ? 's' : ''}`}</span>
              <Icon name="chevron-down" size={14} />
            </button>
            {expanded && <ReactionEditor template={template} apply={apply} catalog={catalog} trigger={moment.trigger} heading={<span className="ay-v2-sentence"><span className="ay-v2-word">Quand</span><strong className="ay-v2-token">{moment.label.replace(/^À /, '')}</strong></span>} />}
          </li>
        );
      })}
    </ul>
  );
}
